import React, { useState, useCallback, useMemo, useEffect } from "react";
import Plot from "react-plotly.js";
import { dimensions, clusterColors, geneColorMapping } from "../constants";
import {
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputLabel,
  Card,
  CardContent,
  Typography,
  Checkbox,
  ListItemText,
  ButtonGroup,
  Button
} from "@material-ui/core";
import { styled } from "@mui/system";
import { useAudioProfile } from "../contexts/AudioProfileContext";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: "20%",
    maxWidth: "80%",
  },
  formControlPatient: {
    margin: theme.spacing(1),
    width: "40%",
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  container: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "row", // Change to row to align items horizontally
    alignItems: "flex-start", // Align items at the start of the flex container
    justifyContent: "space-around", // Distribute space around items
    height: "100%",
    width: "100%",
    overflowY: "auto",
  },
  plotColumn: {
    display: "flex",
    flexDirection: "column", // Stack items vertically in each column
    justifyContent: "flex-start",
    alignItems: "center",
    height: "100%",
    flex: 1, // Allow columns to grow and fill the container
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "left",
    alignItems: "left",
    marginBottom: theme.spacing(2),
    width: "100%", // Ensure button group takes full width of its container
    flexDirection: "row",
  },
  menuItem: {
    minWidth: "10%",
    maxWidth: "100%",
    color: "black",
  },
  clickedPointItem: {
    height: "30%",
    padding: "10px", // Add padding inside the card for better aesthetics (optional)
    backgroundColor: "white",
    color: "black",
    "&:first-child": {
      backgroundColor: "black",
      color: "white",
    },
    fontSize: "1rem",
  },
  button: {
    backgroundColor: "white",
    color: "black",
    width: "25%",
    height: "50px",
    borderRadius: "5px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)", // Add shadow to create floating effect
    transition: "box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out", // Smooth transition for hover effect
    "&:hover": {
      backgroundColor: "black",
      color: "white",
      boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.3)", // Increase shadow to enhance floating effect on hover
      transform: "translateY(-2px)", // Move button up slightly to enhance floating effect
    },
    "&:disabled": {
      backgroundColor: "#f7f7f7",
      boxShadow: "none", // Remove shadow when button is disabled
    },
    margin: theme.spacing(2),
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  "&.MuiMenuItem-root": {
    color: "black",
    "&:hover": {
      backgroundColor: "black",
      color: "white",
    },
  },
  "&.MuiMenuItem-selected": {
    backgroundColor: "black",
    color: "white",
  },
  width: "100%",
  fontStyle: "italic",
}));

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const ThreeDClustering = ({
  clusteringDataGreedy,
  clusteringDataOriginal,
  geneCountsAfterResampling,
  table_clustering_data,
  hypothesis,
}) => {
  const classes = useStyles();
  const { predictionData } = useAudioProfile();
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [selectedClusteringMethod, setSelectedClusteringMethod] =
    useState("original");
  const [selectedGene, setSelectedGene] = useState(null);
  const [showPercentage, setShowPercentage] = useState(false);
  const [cameraPosition, setCameraPosition] = useState(() => {
    // Attempt to load saved state from local storage
    const saved = localStorage.getItem("threeDClusteringCameraPosition");
    if (saved !== null) {
      try {
        // Attempt to parse the saved JSON string
        return JSON.parse(saved);
      } catch (e) {
        // Log the error to the console
        console.error("Failed to parse camera position from localStorage:", e);
      }
    }
    // Return a default value if parsing fails or if there's no saved state
    return {
      eye: { x: 1.25, y: 1.25, z: 1.25 },
    };
  });
  const [clickedPoints, setClickedPoints] = useState([]);
  const [selectedDropdownGenes, setSelectedDropdownGenes] = useState([]); 
  const [visualizationMethod, setVisualizationMethod] =
    useState("cluster and gene");
  const [showHoverTemplate, setShowHoverTemplate] = useState(true);
  const [sizeMode, setSizeMode] = useState("count"); // New state for size mode
  console.log("Show Hover Template: ", showHoverTemplate);

  let data =
    selectedClusteringMethod === "greedy"
      ? clusteringDataGreedy
      : clusteringDataOriginal;

  // Compute gene centroids from the active dataset so closest-gene lookups
  // always reflect the currently selected view (original vs resampled).
  const geneCentroids = useMemo(() => {
    const acc = {};
    data.forEach((point) => {
      if (!acc[point.gene]) acc[point.gene] = { x: 0, y: 0, z: 0, count: 0 };
      acc[point.gene].x += point.x;
      acc[point.gene].y += point.y;
      acc[point.gene].z += point.z;
      acc[point.gene].count += 1;
    });
    return Object.entries(acc).map(([gene, { x, y, z, count }]) => ({
      gene,
      x: x / count,
      y: y / count,
      z: z / count,
    }));
  }, [data]);
  const clusters = useMemo(
    () => [...new Set(data.map((item) => item.cluster))],
    [data],
  );
  const genes = hypothesis.selectedGenes;

  const [selectedPatient, setSelectedPatient] = useState("No Patient");
  const [plotTitle, setPlotTitle] = useState("Cluster and Gene - Cluster 0");

  const handleClusteringMethodChange = (event) => {
    setSelectedClusteringMethod(event.target.value);
  };

  const handleToggleChange = (event) => {
    setShowPercentage(event.target.checked);
  };

  const handleDropdownGeneChange = (event) => {
    setSelectedDropdownGenes(event.target.value); // Update selected dropdown genes
  };

  // Add an option for "All Patients"
  const allPatientsOption = "No Patient";

  const handlePatientChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedPatient(selectedValue);
  };

  // Function to calculate the percentage of a gene in a cluster
  const calculatePercentage = (gene, clusterCount) => {
    const totalCount = geneCountsAfterResampling[gene] || 1; // Fallback to 1 to avoid division by zero
    return (clusterCount / totalCount) * 100;
  };

  const geneColor = useCallback(
    (gene) => geneColorMapping[gene] || "black",
    [],
  );
  const clusterColor = useCallback(
    (cluster) => clusterColors[cluster] || "black",
    [],
  );

  const barTraces = genes.map((gene) => {
    const geneCounts = clusters.map((cluster) => {
      let clusterPoints = data.filter((point) => point.cluster === cluster);
      // Removed filtering based on selectedGenes
      return clusterPoints.filter((point) => point.gene === gene).length;
    });
    // Conditionally format the name based on the selectedClusteringMethod
    let traceName;
    if (selectedClusteringMethod === "greedy") {
      // For greedy method, include gene, total count, and cluster numbers in the name
      const clustersWithGene = clusters
        .filter((cluster, index) => geneCounts[index] > 0)
        .join(", ");
      traceName = `${gene} (${geneCounts.reduce((a, b) => a + b, 0)}) - Cluster: ${clustersWithGene}`;
    } else {
      // For other methods, just include gene and total count
      traceName = `${gene} (${geneCounts.reduce((a, b) => a + b, 0)})`;
    }
    return {
      x: clusters,
      y: geneCounts,
      name: traceName,
      type: "bar",
      hovertemplate: "Cluster: %{x}<br>Count: %{y}<br>Gene: " + gene,
      marker: {
        color: geneColor(gene),
      },
    };
  });

  // Modify barTraces to show percentages if toggle is on
  const modifiedBarTraces = barTraces.map((trace) => {
    const geneName = trace.name.split(" ")[0]; // Extract the gene name
    if (showPercentage) {
      return {
        ...trace,
        y: trace.y.map((count, index) => calculatePercentage(geneName, count)), // Use the extracted gene name
        hovertemplate: `Cluster: %{x}<br>Percentage: %{y:.2f}%<br>Gene: ${geneName}`,
        name: `${geneName} (${trace.y.reduce((a, b) => a + b, 0)})`, // Add the total count to the name
      };
    }
    return trace;
  });

  // Function to generate traces for table_clustering_data
  const generateTableClusteringTraces = useCallback(() => {
    const tableData = table_clustering_data
      .filter(
        (dataPoint) => !selectedPatient || dataPoint.id === selectedPatient,
      )
      .map((dataPoint) => {
        let predictedGenesV4 = predictionData["audiogenev4"].find(
          (patient) => patient.id === dataPoint.id,
        );
        let predictedGenesV9 = predictionData["audiogenev9"].find(
          (patient) => patient.id === dataPoint.id,
        );
        let predictedGenes = predictedGenesV4 || predictedGenesV9;
        if (predictedGenes) {
          predictedGenes = predictedGenes.genes.slice(0, 3).join(", ");
        } else {
          predictedGenes = "";
        }
        const closestGenes = geneCentroids
          .map((c) => ({
            gene: c.gene,
            distance: Math.sqrt(
              Math.pow(c.x - dataPoint.x, 2) +
              Math.pow(c.y - dataPoint.y, 2) +
              Math.pow(c.z - dataPoint.z, 2)
            ),
          }))
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 3)
          .map((d) => d.gene)
          .join(", ");

        return {
          x: [dataPoint.x],
          y: [dataPoint.y],
          z: [dataPoint.z],
          mode: "markers",
          marker: {
            size: 10,
            color: "red",
          },
          type: "scatter3d",
          name: `User Data: ${dataPoint.id}`,
          text: [
            `ID: ${dataPoint.id}<br>Top 3 Predicted Genes: ${predictedGenes}<br>Closest Gene Instances: ${closestGenes}<br>Cluster: ${dataPoint.cluster}`,
          ],
          hovertemplate: showHoverTemplate ? `%{text}` : "",
        };
      });
    return tableData;
  }, [table_clustering_data, selectedPatient, showHoverTemplate, geneCentroids]);

  // // Modify the handleBarHover function
  // const handleBarHover = (data) => {
  //   if (visualizationMethod === "cluster and gene") {
  //     setSelectedGene(data.points[0].data.name.split(" ")[0]);
  //     setSelectedCluster(data.points[0].x);
  //     setPlotTitle(`Cluster and Gene - Cluster ${data.points[0].x}`); // Update plot title
  //   } else if (visualizationMethod === "byCluster") {
  //     setSelectedCluster(data.points[0].x);
  //     setPlotTitle(`By Cluster - Cluster ${data.points[0].x}`); // Update plot title
  //   }
  //   // For 'clustersVisualization', hovering over the bar chart does nothing
  // };

  // Modify the handleBarClick function
  const handleBarClick = (data) => {
    if (visualizationMethod === "cluster and gene") {
      setSelectedGene(data.points[0].data.name.split(" ")[0]);
      setSelectedCluster(data.points[0].x);
      setPlotTitle(`Cluster and Gene - Cluster ${data.points[0].x}`); // Update plot title
    } else if (visualizationMethod === "byCluster") {
      setSelectedCluster(data.points[0].x);
      setPlotTitle(`By Cluster - Cluster ${data.points[0].x}`); // Update plot title
    }
    // For 'clustersVisualization', clicking on the bar chart does nothing
  };

  // Hard-coded dictionary for gene counts
  const geneCountsDictionary = {
    ACTG1: 161,
    CCDC50: 5,
    CEACAM16: 48,
    COCH: 935,
    COL11A2: 64,
    EYA4: 96,
    DIAPH1: 10,
    GJB2: 45,
    GRHL2: 13,
    GSDME: 369,
    KCNQ4: 611,
    MIRN96: 10,
    MYH14: 4,
    MYH9: 23,
    MYO6: 67,
    MYO7A: 113,
    P2RX2: 6,
    POU4F3: 70,
    REST: 18,
    SLC17A8: 16,
    TECTA: 137,
    TMC1: 57,
    WFS1: 340,
  };

  const generateScatterTraces = useCallback(() => {
    let traces = [];
    const centroidPositions = []; // Initialize centroid positions array here

    switch (visualizationMethod) {
      case "gene only": // New visualization mode for highlighting selected genes
        traces = genes.map((gene) => {
          const genePoints = data.filter((point) => point.gene === gene);
          const isSelected = selectedDropdownGenes.indexOf(gene) > -1; // Check if gene is selected
          const color = isSelected ? geneColor(gene) : "lightgrey"; // Use gene color if selected, otherwise grey

          const count = genePoints.length; // Define count here


          return {
            x: genePoints.map((point) => point.x),
            y: genePoints.map((point) => point.y),
            z: genePoints.map((point) => point.z),
            mode: "markers",
            type: "scatter3d",
            name: `${gene} (${count})`, // Use count for naming
            marker: { size: 6, color: color },
            hoverinfo: !showHoverTemplate ? "text" : undefined,
            hovertemplate: !showHoverTemplate ? "%{text}" : undefined,
            text: genePoints.map((point) => `Gene: ${point.gene}<br>Cluster: ${point.cluster}`),
          };
        });
        break;
      case "gene centroids": // New visualization mode for gene centroids
        const centroidsForGene = {}; // Initialize centroids object here

        // Calculate centroids for each gene
        data.forEach(point => {
          if (!centroidsForGene[point.gene]) {
            centroidsForGene[point.gene] = { x: 0, y: 0, z: 0, count: 0, sumSq: 0 }; // Add sumSq for variance calculation
          }
          centroidsForGene[point.gene].x += point.x;
          centroidsForGene[point.gene].y += point.y;
          centroidsForGene[point.gene].z += point.z;
          centroidsForGene[point.gene].count += 1;
          centroidsForGene[point.gene].sumSq += point.x * point.x + point.y * point.y + point.z * point.z; // Accumulate squared values
        });

        // Create traces for centroids and find nearest centroids
        for (const gene in centroidsForGene) {
          const { x, y, z, count, sumSq } = centroidsForGene[gene];
          const centroidX = x / count;
          const centroidY = y / count;
          const centroidZ = z / count;

          // Calculate variance
          const variance = (sumSq / count) - (centroidX * centroidX + centroidY * centroidY + centroidZ * centroidZ);

          // Adjust size based on selected size mode
          const markerSize = sizeMode === "count" 
            ? Math.max(5, Math.min(20, (count - 4) / (935 - 4) * (20 - 5) + 5)) // Adjust size range based on new min and max
            : Math.max(4, Math.min(20, variance)); // Keep variance size logic as is

          centroidPositions.push({ gene, x: centroidX, y: centroidY, z: centroidZ }); // Store centroid positions

          // Find nearest centroids for hover info
          const distances = centroidPositions.map((endCentroid) => {
            if (gene !== endCentroid.gene) {
              const distance = Math.sqrt(
                Math.pow(endCentroid.x - centroidX, 2) +
                Math.pow(endCentroid.y - centroidY, 2) +
                Math.pow(endCentroid.z - centroidZ, 2)
              );
              return { gene: endCentroid.gene, distance };
            }
            return null; // Skip self-comparison
          }).filter(Boolean); // Remove null values

          // Sort distances and get the 5 closest
          distances.sort((a, b) => a.distance - b.distance);
          const closestFive = distances.slice(0, 5);

          // Create hover text for the centroid
          const hoverText = closestFive.map(
            (d) => `${d.gene}: ${d.distance.toFixed(2)}`
          ).join("<br>");

          const traceName = sizeMode === "count" 
            ? `${gene} (${count})` 
            : `${gene} (${variance.toFixed(2)})`; // Format variance to 2 decimal places

          // console.log(`Gene: ${gene}, Count: ${count}`);

          // Push the trace with the closest centroids in the hover text
          traces.push({
            x: [centroidX], // Average x position
            y: [centroidY], // Average y position
            z: [centroidZ], // Average z position
            mode: "markers",
            type: "scatter3d",
            name: traceName,
            marker: { size: markerSize, color: geneColor(gene) }, // Customize marker size based on count
            hoverinfo: "text",
            text: [`Gene: ${gene}<br>Centroid Position: (${centroidX.toFixed(2)}, ${centroidY.toFixed(2)}, ${centroidZ.toFixed(2)})<br>Closest Centroids:<br>${hoverText}`],
          });
        }
        break;
      case "clustersVisualization":
        // Logic for "Cluster Visualization"
        traces = clusters.map((cluster) => {
          let clusterPoints = data.filter((point) => point.cluster === cluster);
          return {
            x: clusterPoints.map((point) => point.x),
            y: clusterPoints.map((point) => point.y),
            z: clusterPoints.map((point) => point.z),
            mode: "markers",
            type: "scatter3d",
            name: `Cluster ${cluster}`,
            marker: {
              size: 3,
              color: clusterColor(cluster), // Color for each cluster
            },
            hoverinfo: !showHoverTemplate ? "text" : undefined,
            hovertemplate: !showHoverTemplate ? "%{text}" : undefined,
            text: clusterPoints.map(
              (point) => `Gene: ${point.gene}<br>Cluster: ${point.cluster}`,
            ),
          };
        });
        break;

      case "cluster and gene":
        // Logic for "Cluster and Gene"
        const dynamicGeneCounts = {};
        data.forEach((point) => {
          dynamicGeneCounts[point.gene] = (dynamicGeneCounts[point.gene] || 0) + 1;
        });
        data.forEach((point) => {
          const count = dynamicGeneCounts[point.gene] || 0;
          const color =
            point.cluster === selectedCluster
              ? geneColor(point.gene)
              : "lightgrey";
          const traceIndex = traces.findIndex(
            (trace) => trace.name === `${point.gene} (${count})`,
          );
          if (traceIndex === -1) {
            traces.push({
              x: [point.x],
              y: [point.y],
              z: [point.z],
              mode: "markers",
              type: "scatter3d",
              name: `${point.gene} (${count})`,
              marker: { size: 6, color: [color] },
              hoverinfo: !showHoverTemplate ? "text" : undefined,
              hovertemplate: !showHoverTemplate ? "%{text}" : undefined,
              text: [`Gene: ${point.gene}<br>Cluster: ${point.cluster}`],
            });
          } else {
            traces[traceIndex].x.push(point.x);
            traces[traceIndex].y.push(point.y);
            traces[traceIndex].z.push(point.z);
            traces[traceIndex].marker.color.push(color);
            traces[traceIndex].text.push(
              `Gene: ${point.gene}<br>Cluster: ${point.cluster}`,
            );
          }
        });
        break;

      case "byCluster":
        // Logic for "By Cluster"
        if (selectedCluster !== null) {
          // Initialize an object to hold two sets of points: those in the selected cluster and those not
          let inClusterPoints = {
            x: [],
            y: [],
            z: [],
            marker: { color: [], size: 3 },
          };
          let outOfClusterPoints = {
            x: [],
            y: [],
            z: [],
            marker: { color: [], size: 3 },
          };

          // Iterate over each data point to classify it as in-cluster or out-of-cluster
          data.forEach((point) => {
            if (point.cluster === selectedCluster) {
              inClusterPoints.x.push(point.x);
              inClusterPoints.y.push(point.y);
              inClusterPoints.z.push(point.z);
              inClusterPoints.marker.color.push("red"); // Highlight in-cluster points in red
            } else {
              outOfClusterPoints.x.push(point.x);
              outOfClusterPoints.y.push(point.y);
              outOfClusterPoints.z.push(point.z);
              outOfClusterPoints.marker.color.push("lightgrey"); // Grey out out-of-cluster points
            }
          });

          // Add the in-cluster points trace
          traces.push({
            ...inClusterPoints,
            mode: "markers",
            type: "scatter3d",
            name: `Cluster ${selectedCluster} (In)`,
            hoverinfo: !showHoverTemplate ? "text" : undefined,
            hovertemplate: !showHoverTemplate ? "%{text}" : undefined,
            text: data
              .filter((point) => point.cluster === selectedCluster)
              .map(
                (point) => `Gene: ${point.gene}<br>Cluster: ${point.cluster}`,
              ),
          });

          // Add the out-of-cluster points trace
          traces.push({
            ...outOfClusterPoints,
            mode: "markers",
            type: "scatter3d",
            name: `Cluster ${selectedCluster} (Out)`,
            hoverinfo: !showHoverTemplate ? "text" : undefined,
            hovertemplate: !showHoverTemplate ? "%{text}" : undefined,
            text: data
              .filter((point) => point.cluster !== selectedCluster)
              .map(
                (point) => `Gene: ${point.gene}<br>Cluster: ${point.cluster}`,
              ),
          });
        } else {
          // If no cluster is selected, default to light grey for all points
          traces.push({
            x: data.map((point) => point.x),
            y: data.map((point) => point.y),
            z: data.map((point) => point.z),
            mode: "markers",
            type: "scatter3d",
            name: "All Clusters",
            marker: {
              size: 3,
              color: "lightgrey",
            },
            hoverinfo: !showHoverTemplate ? "text" : undefined,
            hovertemplate: !showHoverTemplate ? "%{text}" : undefined,
            text: data.map(
              (point) => `Gene: ${point.gene}<br>Cluster: ${point.cluster}`,
            ),
          });
        }
        break;
      default:
        // Handle default case or other visualization methods if any
        break;
    }

    // Calculate distances and find closest centroids for hover info
  centroidPositions.forEach((startCentroid) => {
    const distances = centroidPositions.map((endCentroid) => {
      if (startCentroid.gene !== endCentroid.gene) {
        const distance = Math.sqrt(
          Math.pow(endCentroid.x - startCentroid.x, 2) +
          Math.pow(endCentroid.y - startCentroid.y, 2) +
          Math.pow(endCentroid.z - startCentroid.z, 2)
        );
        return { gene: endCentroid.gene, distance };
      }
      return null; // Skip self-comparison
    }).filter(Boolean); // Remove null values
    // console.log(distances)

    // Sort distances and get the 5 closest
    distances.sort((a, b) => a.distance - b.distance);
    const closestFive = distances.slice(0, 5);

    // Create hover text for the centroid
    const hoverText = closestFive.map(
      (d) => `${d.gene}: ${d.distance.toFixed(2)}`
    ).join("<br>");

    // Update the trace with the closest centroids in the hover text
    const traceIndex = traces.findIndex(trace => trace.name === `Centroid: ${startCentroid.gene}`);
    if (traceIndex !== -1) {
      traces[traceIndex].text.push(
        `Closest Centroids:<br>${hoverText}`
      );
    }
  });

    return traces;
  }, [
    data,
    visualizationMethod,
    selectedCluster,
    clusters,
    geneColor,
    clusterColor,
    selectedDropdownGenes,
    showHoverTemplate,
    sizeMode
  ]);

  const handleRelayout = useCallback((event) => {
    // Check if the relayout event includes camera changes
    if (event["scene.camera"]) {
      const newCameraPosition = {
        eye: event["scene.camera"].eye, // This controls the zoom
        up: event["scene.camera"].up,
        center: event["scene.camera"].center,
        projection: event["scene.camera"].projection,
      };

      // Use the debounced function to handle saving the camera position
      debouncedSaveCameraPosition(newCameraPosition);
    }
  }, []); // Dependencies array is empty if no external dependencies are needed

  // Create a debounced version of the function that saves the camera position
  const debouncedSaveCameraPosition = debounce((newCameraPosition) => {
    // Save the new camera position to localStorage or elsewhere
    setCameraPosition(newCameraPosition);
    localStorage.setItem(
      "threeDClusteringCameraPosition",
      JSON.stringify(newCameraPosition),
    );
  }, 150); // Debounce for 100 ms

  const handleClickedPoint = (event) => {
    // Assuming `event.points` is an array of points, where each point has information about the click event
    if (event.points && event.points.length > 0) {
      const clickedPoint = event.points[0]; // Get the first (and likely only) clicked point
      console.log("Clicked point data:", clickedPoint);
      // Use the point's index to access its specific text information
      const pointIndex = clickedPoint.pointNumber; // This might vary based on the event object structure
      let pointText = clickedPoint.data.text[pointIndex]; // Access the specific text for the clicked point

      // Remove HTML tags from the pointText
      pointText = pointText.replace(/<br>/g, ", "); // Replace <br> with a space
      pointText = pointText.replace(/<[^>]*>/g, ""); // Remove any other HTML tags
      // Now you can use `pointText` which contains only the information for the clicked point
      // For example, updating state or displaying the information somewhere in the UI
      setClickedPoints([pointText, ...clickedPoints]); // Assuming you're storing clicked points in a state
    }
  };

  const resetView = () => {
    debouncedSaveCameraPosition({
      eye: { x: 1.25, y: 1.25, z: 1.25 },
    });
  };

  // Ensure scatter traces are initialized on component mount and when dependencies change
  const modifiedScatterTraces = useMemo(() => {
    const scatterTraces = generateScatterTraces();
    const tableTraces = generateTableClusteringTraces();
    return [...scatterTraces, ...tableTraces];
  }, [generateScatterTraces, generateTableClusteringTraces]);

  // useMemo hook to memoize the plot layout configuration
  const plotLayout = useMemo(
    () => ({
      title: plotTitle,
      scene: {
        xaxis: { title: "X" },
        yaxis: { title: "Y" },
        zaxis: { title: "Z" },
        camera: cameraPosition, // Use the memoized cameraPosition state
      },
      margin: {
        l: 0,
        r: 260,
        b: 0,
        t: 100,
      },
      height: dimensions.height / 1.5,
      width: dimensions.width / 2,
      hovermode: "closest",
      hoverlabel: {
        bgcolor: "white",
        font: { color: "black" },
      },
    }),
    [plotTitle, cameraPosition],
  ); // Dependencies

  // Ensure scatter traces are initialized on component mount
  useEffect(() => {
    generateScatterTraces();
  }, [generateScatterTraces]);

  return (
    <div className={classes.container}>
      <div className={classes.plotColumn}>
        <div className={classes.buttonGroup}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="clustering-method-label">
              Clustering Method
            </InputLabel>
            <Select
              labelId="clustering-method-label"
              id="clustering-method-select"
              value={selectedClusteringMethod}
              onChange={handleClusteringMethodChange}
              label="Clustering Method"
            >
              <StyledMenuItem value="original" className={classes.menuItem}>
                Original Clustering
              </StyledMenuItem>
              <StyledMenuItem value="greedy" className={classes.menuItem}>
                Greedy Clustering
              </StyledMenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="visualization-method-label">
              Visualization Method
            </InputLabel>
            <Select
              labelId="visualization-method-label"
              id="visualization-method-select"
              value={visualizationMethod}
              onChange={(event) => {
                setVisualizationMethod(event.target.value);
                // Update plot title based on selected visualization method
                if (event.target.value === "gene only") {
                  setPlotTitle("Gene Only");
                } else if (event.target.value === "gene centroids") {
                  setPlotTitle("Gene Centroids");
                } else {
                  setPlotTitle("Cluster and Gene - Cluster 0"); // Default title for other methods
                }
              }}
            >
              <StyledMenuItem
                value="cluster and gene"
                className={classes.menuItem}
              >
                Cluster and Gene
              </StyledMenuItem>
              <StyledMenuItem value="byCluster" className={classes.menuItem}>
                By Cluster
              </StyledMenuItem>
              <StyledMenuItem
                value="clustersVisualization"
                className={classes.menuItem}
              >
                Clusters Visualization
              </StyledMenuItem>
              <StyledMenuItem
                value="gene only"
                className={classes.menuItem}
              >
                Gene Only
              </StyledMenuItem>
              <StyledMenuItem
                value="gene centroids"
                className={classes.menuItem}
              >
                Gene Centroids
              </StyledMenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch checked={showPercentage} onChange={handleToggleChange} />
            }
            label="Show Percentage"
          />
        </div>
        <div className={classes.buttonGroup}>
          <FormControl
            variant="outlined"
            className={classes.formControlPatient}
          >
            <InputLabel id="patient-label">Patient</InputLabel>
            <Select
              labelId="patient-label"
              id="patient-select"
              value={selectedPatient}
              onChange={handlePatientChange}
              label="Patient"
            >
              {/* Add an option for "All Patients" */}
              <StyledMenuItem
                value={allPatientsOption}
                className={classes.menuItem}
              >
                No Patient
              </StyledMenuItem>
              {/* Aggregate and map through the predictionData to create a MenuItem for each unique patient across all versions */}
              {Object.values(predictionData)
                .flatMap((versionData) =>
                  versionData.map((patient) => patient.id),
                )
                .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
                .sort((a, b) => {
                  const numberA = parseInt(a.split(" ")[1], 10); // Extract number from format "ID #"
                  const numberB = parseInt(b.split(" ")[1], 10);
                  return numberA - numberB; // Sort numerically
                })
                .map((patientId) => (
                  <StyledMenuItem
                    key={patientId}
                    value={patientId}
                    className={classes.menuItem}
                  >
                    {patientId}
                  </StyledMenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
        <Plot
          data={modifiedBarTraces}
          layout={{
            title: "Gene Distribution in Clusters",
            barmode: "stack",
            xaxis: { title: "Clusters" },
            yaxis: { title: showPercentage ? "Percentage" : "Count" },
            margin: { l: 50, r: 275, b: 100 },
            height: dimensions.height / 1.5,
            width: dimensions.width / 2.25,
          }}
          onClick={handleBarClick}
          hoverlabel={{ bgcolor: "white", font: { color: "black" } }}
        />
      </div>
      <div className={classes.plotColumn}>
        <div className={classes.buttonGroup}>
          <Card
            style={{
              maxHeight: "175px",
              width: "100%",
              overflowY: "auto", // Allows for scrolling
              backgroundColor: "white",
              marginRight: "20px",
              display: "flex",
              flexDirection: "column",
              padding: "5px",
            }}
          >
            <Typography
              variant="h9"
              style={{
                marginBottom: "10px",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              Clicked Points
            </Typography>
            {clickedPoints
              .slice()
              .slice(0, 15)
              .map((pointInfo, index) => (
                <CardContent
                  key={index}
                  className={classes.clickedPointItem}
                  style={{
                    backgroundColor: index === 0 ? "black" : "white",
                    color: index === 0 ? "white" : "black",
                    fontSize: "1rem",
                  }}
                >
                  {pointInfo}
                </CardContent>
              ))}
          </Card>
          <FormControlLabel
            control={
              <Switch
                checked={showHoverTemplate}
                onChange={() => setShowHoverTemplate(!showHoverTemplate)}
              />
            }
            label="Show Hover Template"
            style={{ alignSelf: "flex-start" }} // Adjust positioning as needed
          />
          <Button
            variant="contained"
            onClick={resetView}
            className={classes.button}
          >
            Reset View
          </Button>
          <FormControl className={classes.formControl}>
            <InputLabel id="dropdown-gene-select-label">Select Genes</InputLabel>
            <Select
              labelId="dropdown-gene-select-label"
              multiple
              value={selectedDropdownGenes} // Use the new state variable
              onChange={handleDropdownGeneChange} // Use the new handler
              renderValue={(selected) => selected.join(", ")}
              className={classes.select}
            >
              {genes.map((gene) => ( // Assuming 'genes' is an array of available genes
                <StyledMenuItem key={gene} value={gene}>
                  <Checkbox
                    checked={selectedDropdownGenes.indexOf(gene) > -1} // Check if gene is selected
                  />
                  <ListItemText primary={gene} />
                </StyledMenuItem>
              ))}
        </Select>
      </FormControl>
        </div>
        {/* Conditionally render the button group based on the selected visualization method */}
        {visualizationMethod === "gene centroids" && (
          <ButtonGroup variant="contained" color="white">
            <Button onClick={() => setSizeMode("count")}>Size by Count</Button>
            <Button onClick={() => setSizeMode("variance")}>Size by Variance</Button>
          </ButtonGroup>
        )}
        <Plot
          data={modifiedScatterTraces} // Use modifiedScatterTraces here
          layout={plotLayout}
          onRelayout={handleRelayout}
          onClick={(event) => {
            handleClickedPoint(event);
          }}
        />
      </div>
    </div>
  );
};

export default ThreeDClustering;
